import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { saveBlob } from "@/ajax/documents/saveBlob";
import ImageViewer from "@/components/MediaViewer/ImageViewer";
import PDFViewer from "@/components/PDFViewer/PDFViewer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { getReceiptUrl } from "../utils/receipt-utils";

export interface ExpenseReceiptPreview {
  id: string;
  fileName: string;
}

interface ExpenseReceiptPreviewDialogProps {
  receipt: ExpenseReceiptPreview | null;
  onClose: () => void;
}

export function ExpenseReceiptPreviewDialog({
  receipt,
  onClose,
}: ExpenseReceiptPreviewDialogProps) {
  const [signedReceipt, setSignedReceipt] = useState<{
    receiptId: string;
    url: string;
  } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    setSignedReceipt(null);
    setLoadError(false);

    if (!receipt) {
      return () => undefined;
    }

    void getReceiptUrl(receipt.id)
      .then((signedUrl) => {
        if (active) {
          setSignedReceipt({ receiptId: receipt.id, url: signedUrl });
        }
      })
      .catch((error) => {
        console.error("Failed to load receipt:", error);
        if (active) {
          setLoadError(true);
        }
      });

    return () => {
      active = false;
    };
  }, [receipt]);

  const url =
    receipt && signedReceipt?.receiptId === receipt.id
      ? signedReceipt.url
      : null;
  const extension = receipt?.fileName.split(".").pop()?.toLowerCase();
  const isPdf = extension === "pdf";
  const isImage = ["jpg", "jpeg", "png", "heic"].includes(extension ?? "");

  const handleDownload = async () => {
    if (!receipt || !url) {
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download receipt");
      }
      await saveBlob({
        blob: await response.blob(),
        fileName: receipt.fileName,
      });
    } catch (error) {
      console.error("Failed to download receipt:", error);
      toast.error("Failed to download receipt");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={receipt !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[95vh] overflow-auto pt-10 sm:max-w-3xl">
        <DialogTitle className="truncate pr-8 text-sm font-medium text-slate-800">
          {receipt?.fileName ?? "Receipt"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Preview and download the expense receipt
        </DialogDescription>

        {!url && !loadError && (
          <div className="flex h-64 items-center justify-center rounded-lg bg-slate-50">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {loadError && (
          <div className="flex h-64 flex-col items-center justify-center rounded-lg bg-slate-50 text-center">
            <FileText className="mb-3 h-10 w-10 text-slate-400" />
            <p className="font-medium text-slate-700">
              Receipt preview unavailable
            </p>
            <p className="text-sm text-slate-500">
              Close the dialog and try again.
            </p>
          </div>
        )}

        {url && isPdf && <PDFViewer url={url} containerClassName="h-[65vh]" />}
        {url && isImage && (
          <ImageViewer
            url={url}
            alt={receipt?.fileName ?? "Expense receipt"}
            containerClassName="h-[65vh]"
          />
        )}
        {url && !isPdf && !isImage && (
          <div className="flex h-64 flex-col items-center justify-center rounded-lg bg-slate-50 text-center">
            <FileText className="mb-3 h-10 w-10 text-slate-400" />
            <p className="font-medium text-slate-700">
              Preview is not available for this file type
            </p>
            <p className="text-sm text-slate-500">
              Download the receipt to view it.
            </p>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={() => void handleDownload()}
          disabled={!url || isDownloading}
          className="min-h-11 w-full"
        >
          {isDownloading ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : (
            <Download className="mr-2 h-5 w-5" />
          )}
          Download
        </Button>
      </DialogContent>
    </Dialog>
  );
}
